import React, { useMemo } from "react";
import { CollectionsEvolutionChart } from "@/components/dashboard/CollectionsEvolutionChart";
import { DashboardSection } from "@/components/dashboard/DashboardSection";
import { KpiCardsRow } from "@/components/dashboard/overview/KpiCardsRow";
import { KpiCardsRowSkeleton } from "@/components/dashboard/overview/KpiCardsRowSkeleton";
import { SuccessRateWidget } from "@/components/dashboard/overview/SuccessRateWidget";
import { VolumeCollectedWidget } from "@/components/dashboard/overview/VolumeCollectedWidget";
import { CarbonByMaterialWidget } from "@/components/dashboard/overview/CarbonByMaterialWidget";
import { isCarbonByMaterialEmpty } from "@/components/dashboard/overview/carbonTreemapLayout";
import { CollectsByOriginWidget } from "@/components/dashboard/overview/CollectsByOriginWidget";
import { DashboardFilterBar } from "@/components/shared/filters";
import {
  toLegacyFilterState,
  useDashboardFilters,
} from "@/hooks/useDashboardFilters";
import {
  useDashboardTotals,
  useDashboardByMaterialPerDay,
  useDashboardFaultReasons,
  useDashboardVolumeByEstablishment,
  useDashboardCarbonByMaterial,
  useDashboardCollectsByOrigin,
} from "@/hooks/useDashboardApiData";
import { useUnifiedAuth } from "@/hooks/useUnifiedAuth";
import { useGatewayMaterials } from "@/hooks/useGatewayMaterials";
import { buildMaterialUnitMap, splitTotalsByUnit } from "@/utils/materialUnit";
import { useAppSelector } from "@/store/hooks";
import { useDemoMode } from "@/contexts/DemoModeContext";
import {
  maskDashboardTotals,
  maskByMaterialPerDay,
  maskVolumeByEstablishment,
  maskFaultReasons,
  maskCarbonByMaterial,
  maskCollectsByOrigin,
  maskNumericValue,
} from "@/utils/demoMode";

const Dashboard = () => {
  const {
    filters: dashboardFilters,
    setFilters,
    removeFilter,
    clearAll,
  } = useDashboardFilters();

  const { profile } = useUnifiedAuth();
  const { isDemoMode } = useDemoMode();
  const registeredEstablishments = useAppSelector(
    (s) => s.establishmentAccess.establishments,
  );

  /**
   * Os hooks de dados desta tela ainda falam o FilterState antigo. O adaptador
   * é temporário e sai junto com a dashboard atual (GR-665).
   */
  const filters = useMemo(
    () => toLegacyFilterState(dashboardFilters),
    [dashboardFilters],
  );

  /**
   * Cada seção tem sua própria query — se uma falhar (schema, dado, rede), só
   * ela cai pro estado de erro (ver DashboardSection), o resto da tela
   * continua de pé.
   */
  const totalsQuery = useDashboardTotals({ filters });
  const byMaterialPerDayQuery = useDashboardByMaterialPerDay({ filters });
  const faultReasonsQuery = useDashboardFaultReasons({ filters });
  const volumeByEstablishmentQuery = useDashboardVolumeByEstablishment({ filters });
  const carbonByMaterialQuery = useDashboardCarbonByMaterial({ filters });
  const collectsByOriginQuery = useDashboardCollectsByOrigin({ filters });

  /**
   * Modo demo mascara os dados só na borda de exibição — os hooks acima
   * seguem retornando os valores reais, então cache/refetch/lógica de negócio
   * não são afetados por isso.
   */
  const maskedTotals = useMemo(
    () => totalsQuery.data && maskDashboardTotals(totalsQuery.data, isDemoMode),
    [totalsQuery.data, isDemoMode],
  );
  const maskedByMaterialPerDay = useMemo(
    () => maskByMaterialPerDay(byMaterialPerDayQuery.data ?? [], isDemoMode),
    [byMaterialPerDayQuery.data, isDemoMode],
  );
  /**
   * Peso e volume do card saem das linhas por material, separadas pela unidade do cadastro: o
   * `total_weight` pronto soma litros de óleo como kg, e o `total_volume` é esse total × 1,1.
   * Catálogo indisponível → tudo cai em kg (`unitForMaterial`), sem derrubar o card.
   */
  const materialsQuery = useGatewayMaterials();
  const totalsByUnit = useMemo(
    () => splitTotalsByUnit(maskedByMaterialPerDay, buildMaterialUnitMap(materialsQuery.data)),
    [maskedByMaterialPerDay, materialsQuery.data],
  );
  const maskedVolumeByEstablishment = useMemo(
    () => maskVolumeByEstablishment(volumeByEstablishmentQuery.data ?? [], isDemoMode),
    [volumeByEstablishmentQuery.data, isDemoMode],
  );
  const maskedFaultReasons = useMemo(
    () => maskFaultReasons(faultReasonsQuery.data ?? [], isDemoMode),
    [faultReasonsQuery.data, isDemoMode],
  );
  const maskedCarbonByMaterial = useMemo(
    () => maskCarbonByMaterial(carbonByMaterialQuery.data ?? [], isDemoMode),
    [carbonByMaterialQuery.data, isDemoMode],
  );
  const maskedCollectsByOrigin = useMemo(
    () => maskCollectsByOrigin(collectsByOriginQuery.data ?? [], isDemoMode),
    [collectsByOriginQuery.data, isDemoMode],
  );

  /**
   * Decide o layout da linha de baixo (ver JSX): com carbono, ele pareia com
   * Coletas recorrentes (mesma altura); sem carbono, Coletas recorrentes
   * ocupa a linha inteira. Loading/erro contam como "tem carbono" porque
   * ainda não dá pra saber se o período vai vir vazio.
   */
  const showCarbon =
    carbonByMaterialQuery.isLoading ||
    !!carbonByMaterialQuery.error ||
    !isCarbonByMaterialEmpty(carbonByMaterialQuery.data ?? []);

  return (
    <div className="min-h-screen bg-neutral-100 p-4 md:p-6 w-full overflow-x-hidden">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6 min-w-0">
        {/* Fica fora do loading: filtrar de novo não pode sumir com o filtro. */}
        <DashboardFilterBar
          title="Operacional"
          analyticsPrefix="dashboard"
          filters={dashboardFilters}
          onChange={setFilters}
          onRemoveFilter={removeFilter}
          onClearAll={clearAll}
        />

        <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
          <DashboardSection
            isLoading={totalsQuery.isLoading || byMaterialPerDayQuery.isLoading || materialsQuery.isLoading}
            error={totalsQuery.error ?? byMaterialPerDayQuery.error}
            onRetry={() => {
              totalsQuery.refetch();
              byMaterialPerDayQuery.refetch();
            }}
            loadingContent={<KpiCardsRowSkeleton />}
          >
            <KpiCardsRow
              registeredCount={maskNumericValue(registeredEstablishments.length, isDemoMode)}
              withCollectsCount={maskedTotals?.total_establishments_with_collects ?? 0}
              totalCollects={maskedTotals?.total_collects ?? 0}
              variationPercent={maskedTotals?.variation_percent ?? null}
              successRatePercent={maskedTotals?.success_rate ?? 0}
              mtrRatePercent={maskedTotals?.mtr_rate ?? 0}
              // `mtr_rate` nulo = o serviço não sabe (Fase 2); um "0%" afirmaria o que ninguém mediu.
              showMtrRate={!!profile?.permission_role && maskedTotals?.mtr_rate != null}
              totalWeightKg={totalsByUnit.weightKg}
              kgCollects={totalsByUnit.kgCollects}
              totalUnits={maskedTotals?.total_units ?? 0}
              totalVolumeLiters={totalsByUnit.volumeLt}
              ltCollects={totalsByUnit.ltCollects}
            />
          </DashboardSection>

          <DashboardSection
            isLoading={byMaterialPerDayQuery.isLoading}
            error={byMaterialPerDayQuery.error}
            onRetry={() => byMaterialPerDayQuery.refetch()}
            loadingHeight="450px"
          >
            <CollectionsEvolutionChart
              filters={filters}
              dashboardByMaterialPerDay={maskedByMaterialPerDay}
            />
          </DashboardSection>

          {/* Volume coletado e Taxa de sucesso dividem a linha (Figma: 871 +
              279). Cada um tem sua própria DashboardSection: se uma query
              falhar, só aquele card cai pro estado de erro. */}
          {/* As larguras ficam nos wrappers, não dentro dos widgets: o
              skeleton de loading do DashboardSection é `w-full` e, solto na
              linha, esmagaria o card vizinho enquanto uma das queries carrega.

              Lado a lado só a partir de `lg`: no tablet (768px) os 279px fixos
              da taxa de sucesso deixariam menos de 450px para o heatmap, e a
              grade colapsava. Abaixo disso os dois empilham em largura cheia. */}
          <div className="flex flex-col items-stretch gap-4 lg:flex-row">
            <div className="min-w-0 flex-1">
              <DashboardSection
                isLoading={volumeByEstablishmentQuery.isLoading}
                error={volumeByEstablishmentQuery.error}
                onRetry={() => volumeByEstablishmentQuery.refetch()}
                loadingHeight="378px"
              >
                <VolumeCollectedWidget rows={maskedVolumeByEstablishment} />
              </DashboardSection>
            </div>

            <div className="w-full lg:w-[279px] lg:shrink-0">
              <DashboardSection
                isLoading={totalsQuery.isLoading || faultReasonsQuery.isLoading}
                error={totalsQuery.error || faultReasonsQuery.error}
                onRetry={() => {
                  totalsQuery.refetch();
                  faultReasonsQuery.refetch();
                }}
                loadingHeight="378px"
              >
                <SuccessRateWidget
                  successRatePercent={maskedTotals?.success_rate ?? 0}
                  faultReasons={maskedFaultReasons}
                />
              </DashboardSection>
            </div>
          </div>

          {/* Layout muda conforme há dado de carbono no período — `showCarbon`
              só vira `false` depois de confirmar (loading/erro mantêm o
              layout "com carbono", não têm como saber ainda se vai sumir). */}
          {showCarbon ? (
            <>
              {/* Carbono e Coletas recorrentes dividem a linha (Figma node
                  345:16133: 674 + 476, gap 16), mesma altura (`items-stretch`)
                  — os dois cabem bem no mesmo tamanho. Sazonalidade fica
                  sozinha, full width, abaixo. */}
              <div className="flex flex-col items-stretch gap-4 lg:flex-row">
                <div className="min-w-0 flex-1">
                  <DashboardSection
                    isLoading={carbonByMaterialQuery.isLoading}
                    error={carbonByMaterialQuery.error}
                    onRetry={() => carbonByMaterialQuery.refetch()}
                    loadingHeight="352px"
                  >
                    <CarbonByMaterialWidget entries={maskedCarbonByMaterial} />
                  </DashboardSection>
                </div>

                <div className="w-full lg:w-[476px] lg:shrink-0">
                  <DashboardSection
                    isLoading={collectsByOriginQuery.isLoading}
                    error={collectsByOriginQuery.error}
                    onRetry={() => collectsByOriginQuery.refetch()}
                    loadingHeight="352px"
                  >
                    <CollectsByOriginWidget entries={maskedCollectsByOrigin} />
                  </DashboardSection>
                </div>
              </div>

            </>
          ) : (
            /* Sem carbono no período: Coletas recorrentes ocupa a linha
                inteira, usando toda a largura disponível. */
            <>
              <DashboardSection
                isLoading={collectsByOriginQuery.isLoading}
                error={collectsByOriginQuery.error}
                onRetry={() => collectsByOriginQuery.refetch()}
                loadingHeight="352px"
              >
                <CollectsByOriginWidget entries={maskedCollectsByOrigin} />
              </DashboardSection>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
